import type React from "react"

import { Box, Stack, Typography } from "@mui/material"

import type { TypicalWeekSeries } from "./disaggregation.types"
import { enduseColor, enduseLabel } from "./enduse-colors"

const W = 512
const H = 160
const N = 168
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

/** Stacked-area typical week: 11 end-use layers over 168 hourly steps. */
export const TypicalWeekStack: React.FC<{ series: TypicalWeekSeries[] }> = ({
  series,
}) => {
  if (series.length === 0) {
    return null
  }
  // Non-finite guard: any NaN/Infinity point would poison the peak and blank
  // the whole chart. (JSON can't carry NaN, so this is belt-and-suspenders.)
  const kw = (se: TypicalWeekSeries, h: number): number =>
    Number.isFinite(se.points[h]) ? se.points[h] : 0
  // Per-hour cumulative max = peak stacked kW, for the y-scale.
  const peak = Math.max(
    ...Array.from({ length: N }, (_, h) =>
      series.reduce((s, se) => s + kw(se, h), 0)
    ),
    1
  )
  const x = (h: number): number => (h / (N - 1)) * W
  const y = (v: number): number => H - (v / peak) * H

  // Build each layer's closed polygon between its lower and upper cumulative
  // edge. Accumulated functionally (not via a reassigned loop variable) so
  // render stays a pure fold over `series`.
  const { layers } = series.reduce<{
    cumulative: number[]
    layers: { enduse: string; points: string }[]
  }>(
    (acc, se) => {
      const upper = acc.cumulative.map((base, h) => base + kw(se, h))
      const top = upper.map((v, h) => `${x(h).toFixed(1)},${y(v).toFixed(1)}`)
      const bottom = acc.cumulative
        .map((v, h) => `${x(h).toFixed(1)},${y(v).toFixed(1)}`)
        .reverse()
      const points = [...top, ...bottom].join(" ")
      return {
        cumulative: upper,
        layers: [...acc.layers, { enduse: se.enduse, points }],
      }
    },
    { cumulative: new Array<number>(N).fill(0), layers: [] }
  )

  return (
    <Stack spacing={1}>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <svg height={H} viewBox={`0 0 ${W} ${H}`} width="100%">
          {/* weekday gridlines at each day boundary */}
          {DOW.map((d, i) => (
            <line
              key={d}
              stroke="#e2e8f0"
              strokeWidth={1}
              x1={x(i * 24)}
              x2={x(i * 24)}
              y1={0}
              y2={H}
            />
          ))}
          {layers.map((l) => (
            <polygon
              data-testid="stack-layer"
              fill={enduseColor(l.enduse)}
              fillOpacity={0.85}
              key={l.enduse}
              points={l.points}
            />
          ))}
        </svg>
      </Box>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ justifyContent: "space-between", px: 0.5 }}
      >
        {DOW.map((d) => (
          <Typography color="text.secondary" key={d} variant="caption">
            {d}
          </Typography>
        ))}
      </Stack>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {series.map((se) => (
          <Stack
            direction="row"
            key={se.enduse}
            spacing={0.5}
            sx={{ alignItems: "center" }}
          >
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "2px",
                bgcolor: enduseColor(se.enduse),
              }}
            />
            <Typography variant="caption">{enduseLabel(se.enduse)}</Typography>
          </Stack>
        ))}
      </Box>
    </Stack>
  )
}
